/** @format */
import { rgba } from 'emotion-rgba';
import { css } from 'styled-components';

import { PaginationProps } from './index';
import { PaginationVariant } from './pagination.helpers';

/**
 * Base styles for the Pagination component
 */
const variantStyles = ({ variant }: PaginationProps) => {
    switch (variant) {
        case PaginationVariant.DOT:
            return css`
                ol {
                    li {
                        padding: 0 0.3rem;
                    }
                    body:not(.using-mouse) &:focus {
                        button[aria-selected='true'] {
                            box-shadow: 0 0 0 3px ${rgba('#0b002b', 0.2)};
                        }
                    }
                }
            `;
        case PaginationVariant.THUMBNAIL:
            return css`
                ol {
                    body:not(.using-mouse) &:focus {
                        outline: 4px solid var(--colors-border-primary);
                    }
                    li {
                        padding: 0 1rem;
                        cursor: pointer;
                        > div {
                            transition: 250ms opacity ease-out;
                            opacity:0.2;
                        }
                        
                        div.active {
                            opacity: 1;
                    }
                }
            `;
        default:
        case PaginationVariant.NUMBERED:
            return css`
                ol {
                    body:not(.using-mouse) &:focus {
                        background: var(--colors-surface-tertiary);
                        border-radius: 1.2rem;
                    }
                    li {
                        button {
                            &[tabindex] {
                                outline: none;
                            }

                            &[disabled] .icon {
                                border: none;
                                color: var(--colors-text-disabled);
                                background: var(--colors-surface-disabled);
                            }

                            &:focus:not([disabled]),
                            &:active:not([disabled]) {
                                box-shadow: none;
                                .icon {
                                    box-shadow: none;
                                    background: none;
                                }
                            }
                        }

                        > span {
                            display: inline-block;
                            vertical-align: text-bottom;
                            margin-bottom: 1px;
                            width: 2.4rem;
                            text-align: center;
                        }
                    }
                }
            `;
    }
};

/**
 * Base styles for the Pagination component
 */
const baseStyles = (props: PaginationProps) => css`
    ol {
        margin: 0 0.5rem;
        padding: 0;
        line-height: 1rem;
        display: inline-flex;
        list-style-type: none;
        vertical-align: top;
        outline: none;

        li {
            display: inline;
            outline: none;
            &:first-child {
                padding-left: 0;
            }

            &:last-child {
                padding-right: 0;
            }
        }
    }
`;

/**
 * The Pagination component styles
 *
 * @param {PaginationProps} props The pagination properties object
 */
export const styles = (props: PaginationProps) => css`
    ${baseStyles(props)}
    ${variantStyles(props)}
`;
